import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LayoutDashboard, FileText, Wallet, Users, LogOut, Menu, X, Megaphone, CalendarDays, BarChart2, Building2, Briefcase, ChevronDown, Database, Triangle, Search, LayoutGrid, Zap } from 'lucide-react';
import { loadStore, saveStore, subscribeToChanges, markLocalWrite, getDataVersion, snapshotBase } from './utils/db';
import { defaultTheme } from './utils/storage';
import { applyTheme, loadFont } from './utils/theme';
import { Toast, NotificationBell, EnableNotifications } from './components/UI';
import ThemePanel from './components/ThemePanel';
import LoginScreen, { isAuthenticated, getSession } from './components/LoginScreen';
import { restoreSession, signOut, onSignedOut } from './utils/auth';
import DashboardPage from './pages/DashboardPage';
import InvoicesPage from './pages/InvoicesPage';
import FinancePage from './pages/FinancePage';
import HRPage from './pages/HRPage';
import EmployeePortal from './pages/EmployeePortal';
import UpdatesPage from './pages/UpdatesPage';
import HolidayCalendar from './pages/HolidayCalendar';
import ReportsPage from './pages/ReportsPage';
import ParentClientsPage from './pages/ParentClientsPage';
import TasksPage from './pages/TasksPage';
import ContentPage from './pages/ContentPage';
import AdsPage from './pages/AdsPage';
import CompanyAttendancePage from './pages/CompanyAttendancePage';
import MigrationPage from './pages/MigrationPage';
import LetterheadPage from './pages/LetterheadPage';

const ADMIN_NAV = [
  { id:'dashboard', label:'Dashboard',    icon:LayoutDashboard },
  { id:'invoices',  label:'Invoices',     icon:FileText },
  { id:'finance',   label:'Finance',      icon:Wallet },
  { id:'hr',        label:'HR',           icon:Users },
  { id:'updates',   label:'Updates',      icon:Megaphone },
  { id:'holidays',  label:'Holidays',     icon:CalendarDays },
  { id:'reports',   label:'Reports',      icon:BarChart2 },
  { id:'letterhead',label:'Letterhead',   icon:FileText },
  { id:'ads',       label:'Ads',          icon:Zap },
  { id:'agencies',  label:'Agencies',     icon:Building2 },
  { id:'tasks',     label:'Tasks',        icon:Briefcase },
  { id:'content',   label:'Creative Studio', icon:LayoutGrid },
  { id:'migrate',   label:'Migrate Data', icon:Database },
];

// Last-known store, cached locally so reopening the app paints instantly instead of
// showing a full-screen loader every launch. Kept PER SIGNED-IN ACCOUNT: one person's
// data is never painted for someone else on a shared computer, and the old shared copy
// from before staff sign-in (2026-10-07) — which still held money/admin records an
// employee can no longer see, and made an employee's phone try to save them back — is
// deleted and never used.
const STORE_CACHE_PREFIX = 'strategists_store_cache_v2_';
try { localStorage.removeItem('strategists_store_cache_v1'); } catch (e) { /* storage blocked */ }
function storeCacheKey() { const s = getSession(); return s && s.userId ? STORE_CACHE_PREFIX + s.userId : null; }
function readCachedStore() { const k = storeCacheKey(); if (!k) return null; try { const s = localStorage.getItem(k); return s ? JSON.parse(s) : null; } catch { return null; } }
function writeCachedStore(store) { const k = storeCacheKey(); if (!k) return; try { localStorage.setItem(k, JSON.stringify(store)); } catch (e) { /* over quota — skip caching */ } }
function clearCachedStores() {
  try { Object.keys(localStorage).filter(k => k.startsWith(STORE_CACHE_PREFIX)).forEach(k => localStorage.removeItem(k)); } catch (e) { /* ignore */ }
}

// Deep link: a notification push opens `/?calendar=<id>`. Read it once at boot,
// then scrub the URL so a refresh doesn't re-trigger it.
const INITIAL_CAL = (() => {
  try {
    const id = new URLSearchParams(window.location.search).get('calendar');
    if (id) window.history.replaceState({}, '', window.location.pathname);
    return id || null;
  } catch { return null; }
})();

export default function App() {
  const [authed,     setAuthed]    = useState(() => isAuthenticated());
  // The local session copy is only a hint: wait for the real Supabase session before
  // loading any data, so nothing is fetched (or saved) with a dead token.
  const [authReady,  setAuthReady] = useState(false);
  // True once THIS session has the live data. Until then the screen shows the copy
  // saved on this device last time, and nothing may be saved from it (see persist).
  const freshRef = useRef(false);
  const [store,      setStore]     = useState(null);   // null = loading
  const [page,       setPage]      = useState(INITIAL_CAL ? 'content' : 'dashboard');
  const [contentTarget, setContentTarget] = useState(INITIAL_CAL);
  const [toast,      setToast]     = useState(null);
  const [mobileOpen, setMobileOpen]= useState(false);
  const [hrOpen,     setHrOpen]    = useState(false);
  const [loading,    setLoading]   = useState(true);
  const [loadErr,    setLoadErr]   = useState(null);
  const mobileRef  = useRef();
  const unsubRef   = useRef(null);

  // Map of data-version -> the exact server snapshot at that version. An edit's diff
  // base is looked up by the version stamped on the store it was built from (store.__v),
  // so a flush always diffs against the state the user actually saw — even if a realtime
  // load advanced the live cache in between. This is the fix for task completions / fields
  // being reverted and rows vanishing when a lagging tab saves: the base was previously the
  // *current* cache (snapshotBase()), which could be newer than the edited store, producing
  // phantom "field removed" / "row missing" diffs that reverted or deleted live data.
  const baseByVersion = useRef(new Map());
  const recordServerSnapshot = useCallback((data) => {
    const v = getDataVersion();
    try { baseByVersion.current.set(v, JSON.parse(JSON.stringify(data))); } catch (e) { /* skip if too large */ }
    // Keep a deep window of recent version snapshots. When an edit's true base version
    // is evicted, the flush falls back to the live cache (snapshotBase()), which realtime
    // may have advanced PAST the edited store — that stale/newer base is the root of the
    // "phantom edit" reverts (base has a value the drifted store lost → the merge clears it).
    // A larger window makes the correct per-version base available far more often, so a
    // flush diffs against what the user actually saw. Bounded; the store holds no blobs.
    if (baseByVersion.current.size > 25) {
      let oldest = Infinity;
      for (const k of baseByVersion.current.keys()) if (k < oldest) oldest = k;
      baseByVersion.current.delete(oldest);
    }
    return v;
  }, []);

  const session = getSession ? getSession() : null;
  const role    = session?.role || null;

  useEffect(() => {
    let live = true;
    restoreSession().then(s => { if (!live) return; setAuthed(!!s); setAuthReady(true); });
    // Signed out elsewhere, or the session could not be renewed → back to the login screen.
    const off = onSignedOut(() => { setAuthed(false); setStore(null); });
    return () => { live = false; off(); };
  }, []);

  // ── Load from Supabase when authenticated ────────────────────────
  useEffect(() => {
    if (!authReady) return;
    if (!authed) { setLoading(false); return; }
    setLoadErr(null);
    freshRef.current = false;

    // Instant paint from the last cached store — no full-screen loader on reopen. It is
    // flagged __cached (and given no data version) so it can never be saved: see persist.
    const cached = readCachedStore();
    if (cached) {
      setStore({ ...cached, __v: -1, __cached: true });
      const t = defaultTheme();   // PREVIEW: force STR brand (ignore saved theme)
      loadFont(t.font); applyTheme(t);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // Then refresh from Supabase in the background and update quietly.
    loadStore()
      .then(data => {
        const v = recordServerSnapshot(data);
        freshRef.current = true;
        setStore({ ...data, __v: v });
        writeCachedStore(data);
        const theme = defaultTheme();   // PREVIEW: force STR brand (ignore saved theme)
        loadFont(theme.font); applyTheme(theme);
      })
      .catch(err => {
        console.error('loadStore failed:', err);
        setLoadErr(err.message);
        // Render with empty store so app is not completely broken
        setStore({
          clients:[], employees:[], invoices:[], parentClients:[],
          hr:{}, leaveRequests:[], overtimeRequests:[], tasks:[],
          salaryPayments:[], oneTimeExpenses:[], oneTimeRevenue:[],
          subscriptions:[], commissionPayments:[], reports:[],
          holidays:[], updates:[], contentCalendars:[], mockups:[], notifications:[], employeeCredentials:{}, theme: defaultTheme(),
        });
      })
      .finally(() => setLoading(false));

    // Real-time sync — other sessions (employees clocking in, etc.)
    // update the store live without a page refresh
    // Live update from another session. Repaint, but skip the (synchronous) cache
    // re-serialize on this hot path — the cache is refreshed on our own writes and
    // on the next load, so it doesn't need to run on every incoming change.
    unsubRef.current = subscribeToChanges(updated => {
      const v = recordServerSnapshot(updated);
      setStore({ ...updated, __v: v });
    });
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [authed, authReady]); // eslint-disable-line

  // ── Click-outside closes sidebar ─────────────────────────────────
  useEffect(() => {
    const h = e => { if (mobileRef.current && !mobileRef.current.contains(e.target)) setMobileOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── persist — instant optimistic UI, debounced heavy write ───────
  // setStore is cheap; the expensive parts (localStorage JSON.stringify of the
  // whole store + per-table diff in saveStore) are debounced so typing in a
  // form (e.g. Creative Studio) doesn't block the main thread every keystroke.
  const persistTimer  = useRef(null);
  const pendingStore  = useRef(null);
  const pendingBaseVersion = useRef(0);   // data version this pending write was based on
  // Explicit deletions accumulate here between flushes. saveStore deletes ONLY
  // rows whose id is listed here — never rows merely absent from a (possibly stale)
  // store snapshot — so a lagging tab can never wipe records it simply hadn't loaded.
  const pendingDeletes = useRef({});
  // The server snapshot each pending edit was made against (its correct diff base).
  const pendingBase = useRef(null);
  const flushPersist = useCallback(() => {
    if (persistTimer.current) { clearTimeout(persistTimer.current); persistTimer.current = null; }
    const toSave = pendingStore.current;
    if (!toSave) return Promise.resolve();
    // Diff base = the server snapshot captured when this edit was made. Diffing the
    // edited store against its OWN base pushes only the fields the user changed and
    // deletes only rows the user removed — so a lagging tab can't revert another
    // session's field or wipe rows it never loaded, and the user's own edit is never
    // dropped. No base yet (before the first server load) => don't push (would
    // overwrite the DB with a stale cached snapshot); wait for the load.
    const base = pendingBase.current || snapshotBase();
    if (!base) { persistTimer.current = setTimeout(flushPersist, 400); return Promise.resolve(); }
    pendingStore.current = null;
    pendingBase.current = null;
    writeCachedStore(toSave);
    const deletedIds = pendingDeletes.current; pendingDeletes.current = {};
    return saveStore(toSave, { base, deletedIds }).catch(err => {
      console.error('saveStore failed:', err, err && err.failed, err && err.denied);
      // Refused by the server (this account isn't allowed to make that change): retrying
      // can't help, so say so and don't loop. Anything else is retried below.
      if (err && err.retryable === false) {
        showToast("That change isn't allowed for your account, so it wasn't saved. Refresh the page.", 'error');
        return;
      }
      showToast("Some changes didn't save — check your connection. Retrying…", 'error');
      // Never drop an edit: if nothing newer is queued, queue this one again (its rows
      // still differ from the versions they were copied from, so they'll be re-sent;
      // deletions are idempotent). A newer pending store already contains these edits.
      if (!pendingStore.current) {
        pendingStore.current = toSave;
        pendingBase.current = base;
        for (const tbl of Object.keys(deletedIds || {})) {
          const d = deletedIds[tbl];
          if (Array.isArray(d)) pendingDeletes.current[tbl] = (pendingDeletes.current[tbl] || []).concat(d);
          else if (d && typeof d === 'object') {
            const bucket = pendingDeletes.current[tbl] || (pendingDeletes.current[tbl] = {});
            for (const pid of Object.keys(d)) bucket[pid] = (bucket[pid] || []).concat(d[pid] || []);
          }
        }
        if (persistTimer.current) clearTimeout(persistTimer.current);
        persistTimer.current = setTimeout(flushPersist, 10000);
      }
    });
  }, []); // eslint-disable-line
  const persist = useCallback((updated, opts) => {
    // Screen-only update (e.g. showing a clock-in the server already recorded).
    if (opts && opts.localOnly) { setStore(updated); return; }
    // Never save from the copy kept on this device from last time — the live data hasn't
    // arrived yet (or couldn't load), or this edit was built on that old copy. Saving it
    // would compare the old copy with the live data and put back everything others
    // changed since: Hamza's phone, opened and clocked in within two seconds, undid a
    // client's mockup reorder, to-do ticks and task notes this way (2026-10-07/08).
    if (!freshRef.current || (updated && updated.__cached)) {
      setToast({ msg: 'One moment — the latest data is still loading. Please try that again in a second.', type: 'error' });
      setTimeout(() => setToast(null), 3500);
      return;
    }
    setStore(updated);          // instant UI update (cheap — just a ref swap)
    markLocalWrite();           // keep realtime from clobbering un-saved edits
    pendingStore.current = updated;
    // Diff base = the server snapshot for the version THIS edited store was built from
    // (store.__v), looked up in baseByVersion — NOT the current cache, which realtime may
    // have advanced past this edit. Diffing against the version the user actually saw is
    // what stops a lagging flush from reverting another session's completion/field or
    // deleting a row it never loaded, while still writing the user's own change. Falls back
    // to the live snapshot only for an unversioned store (e.g. the cached boot store).
    const editVersion = (updated && updated.__v != null) ? updated.__v : getDataVersion();
    pendingBase.current = baseByVersion.current.get(editVersion) || snapshotBase();
    if (opts && opts.deletedIds) {
      for (const tbl of Object.keys(opts.deletedIds)) {
        const incoming = opts.deletedIds[tbl];
        if (Array.isArray(incoming)) {
          // Row-level deletions:  { tasks: [id, ...] }
          pendingDeletes.current[tbl] = (pendingDeletes.current[tbl] || []).concat(incoming);
        } else if (incoming && typeof incoming === 'object') {
          // Sub-item deletions, keyed by the row they live in:
          //   { mockupCells:   { [mockupId]: [cellId, ...] } }
          //   { calendarPosts: { [calId]: [postId, ...] }, calendarGrids: { [calId]: [gridId, ...] } }
          // These let the merge RPCs delete ONLY what the user actually removed instead of
          // inferring a deletion from "present in my base, absent from my payload" — which is
          // what let a stale tab wipe a whole mockup row / calendar row (2026-08-27).
          const bucket = pendingDeletes.current[tbl] || (pendingDeletes.current[tbl] = {});
          for (const parentId of Object.keys(incoming)) {
            bucket[parentId] = (bucket[parentId] || []).concat(incoming[parentId] || []);
          }
        }
      }
    }
    pendingBaseVersion.current = editVersion;   // the version this edit is based on
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(flushPersist, 450);
  }, [flushPersist]);

  // Flush any pending write when the tab is hidden/closed so nothing is lost.
  useEffect(() => {
    const onHide = () => { if (document.hidden) flushPersist(); };
    window.addEventListener('beforeunload', flushPersist);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('beforeunload', flushPersist); document.removeEventListener('visibilitychange', onHide); };
  }, [flushPersist]);

  const showToast = useCallback((msg, type='success') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 3500);
  }, []);

  // Signed back in: send anything that was waiting to save straight away (a save
  // refused while signed out stays queued — it is never dropped).
  function handleLogin()  { setAuthed(true); setTimeout(() => flushPersist(), 0); }
  // Sign out: let any pending save finish first (it needs the signed-in token), then end
  // the Supabase session and drop the locally cached data.
  async function handleLogout() {
    await Promise.race([flushPersist(), new Promise(r => setTimeout(r, 4000))]);
    await signOut();
    clearCachedStores();
    setAuthed(false); setStore(null); setPage('dashboard');
  }
  function saveTheme(theme) { const u={...store,theme}; persist(u); applyTheme(theme); showToast('Appearance saved.'); }
  function navigate(id)  { setPage(id); setMobileOpen(false); }

  // ── Notifications (admin recipient id = 'admin') ─────────────────
  const myNotifs = (store?.notifications || [])
    .filter(n => n.empId === 'admin')
    .slice().sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||''));
  function markNotifsRead() {
    persist({ ...store, notifications: (store.notifications||[]).map(n => n.empId==='admin' ? {...n, read:true} : n) });
  }
  function clearNotifs() {
    persist({ ...store, notifications: (store.notifications||[]).filter(n => n.empId!=='admin') });
  }
  function openNotif(n) {
    if (n?.kind === 'content' && n.taskId) { setContentTarget(n.taskId); navigate('content'); }
  }

  // ── Auth gate ─────────────────────────────────────────────────────
  if (!authReady) return null;
  if (!authed) return <LoginScreen onLogin={handleLogin} />;

  // ── Loading state — brand splash (matches the PWA launch screen) ──
  if (loading || !store) return (
    <div style={{minHeight:'100vh',background:'#0A0A0C',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',position:'relative',overflow:'hidden'}}>
      {/* soft brass glow */}
      <div style={{position:'absolute',top:'40%',left:'50%',transform:'translate(-50%,-50%)',width:460,height:460,borderRadius:'50%',background:'radial-gradient(circle,rgba(169,137,83,0.10),transparent 68%)',pointerEvents:'none'}}/>
      {/* STR lockup */}
      <div style={{position:'relative',animation:'sFloat 2.6s ease-in-out infinite',fontFamily:"'Bodoni Moda','Didot',Georgia,serif",fontWeight:400,fontSize:46,letterSpacing:'0.32em',textIndent:'0.32em',color:'#EFEBE4',lineHeight:1}}>STR</div>
      <div style={{position:'relative',width:44,height:1,background:'#A98953',opacity:0.55,margin:'20px 0 13px'}}/>
      <div style={{position:'relative',fontFamily:"'JetBrains Mono',ui-monospace,monospace",fontSize:10,fontWeight:400,letterSpacing:'0.3em',textTransform:'uppercase',color:'rgba(239,235,228,0.42)'}}>Strategists</div>
      {/* indeterminate progress bar */}
      <div style={{position:'relative',marginTop:34,width:150,height:2,background:'rgba(239,235,228,0.10)',overflow:'hidden'}}>
        <div style={{position:'absolute',height:'100%',width:'42%',background:'#A98953',animation:'sSlide 1.25s ease-in-out infinite'}}/>
      </div>
      {loadErr && <div style={{position:'relative',marginTop:18,fontSize:12,color:'#C08079',maxWidth:320,textAlign:'center'}}>Error: {loadErr}</div>}
      <style>{`@keyframes sFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}@keyframes sSlide{0%{left:-42%}100%{left:100%}}`}</style>
    </div>
  );

  // ── Employee portal ───────────────────────────────────────────────
  if (role === 'employee') return <EmployeePortal store={store} persist={persist} onLogout={handleLogout} initialCalendarId={INITIAL_CAL} />;

  const theme = store.theme || defaultTheme();
  const navLabel = ADMIN_NAV.find(n=>n.id===page)?.label || (page==='attendance'?'Company Attendance':page);

  const renderNav = (compact=false) => ADMIN_NAV.map(({id,label,icon:Icon})=>{
    if (id==='hr') return (
      <React.Fragment key={id}>
        <button onClick={()=>setHrOpen(o=>!o)} className={`nav-item${(page==='hr'||page==='attendance')?' active':''}`}>
          <Icon size={18}/><span style={{flex:1}}>Human Resources</span>
          <ChevronDown size={14} style={{transform:hrOpen?'rotate(180deg)':'none',transition:'transform 0.2s'}}/>
        </button>
        {hrOpen && (
          <div className="nav-sub">
            <button onClick={()=>navigate('hr')} className={`nav-subitem${page==='hr'?' active':''}`}><Icon size={15}/>HR Management</button>
            <button onClick={()=>navigate('attendance')} className={`nav-subitem${page==='attendance'?' active':''}`}><Users size={15}/>Company Attendance</button>
          </div>
        )}
      </React.Fragment>
    );
    return (
      <button key={id} onClick={()=>navigate(id)} className={`nav-item${page===id?' active':''}`}>
        <Icon size={18}/>{label}
      </button>
    );
  });

  const SidebarInner = (
    <>
      <div className="sidebar-brand str-lockup">
        <div className="str-mark">STR</div>
        <div className="str-hair"></div>
        <div className="str-desc">Strategists</div>
      </div>
      <nav className="nav-list">{renderNav()}</nav>
      <div className="sidebar-foot">
        <ThemePanel theme={theme} onSave={saveTheme}/>
        <div className="side-user" style={{marginTop:8}}>
          <div className="uav">AD</div>
          <div className="uinfo">
            <div className="uname">Admin</div>
            <div className="urole">Business Manager</div>
          </div>
          <button onClick={handleLogout} title="Sign out" style={{background:'transparent',border:'none',cursor:'pointer',color:'var(--text3)',display:'flex',padding:4}}>
            <LogOut size={17}/>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="app-shell">
      {/* Fixed sidebar (desktop) / drawer (mobile) */}
      <aside ref={mobileRef} className={`sidebar${mobileOpen?' open':''}`}>{SidebarInner}</aside>
      {mobileOpen && <div onClick={()=>setMobileOpen(false)} style={{position:'fixed',inset:0,background:'rgba(10,10,12,0.6)',zIndex:55}}/>}

      <div className="content-area">
        {/* Top bar */}
        <header className="topbar">
          <button className="hamburger" onClick={()=>setMobileOpen(o=>!o)} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:8,cursor:'pointer',color:'var(--text2)',alignItems:'center'}}>
            {mobileOpen?<X size={18}/>:<Menu size={18}/>}
          </button>
          <div className="topsearch">
            <Search size={16}/>
            <input placeholder="Search operations…" readOnly/>
          </div>
          <div style={{flex:1}}/>
          <span className="hide-mobile" style={{fontFamily:'var(--mono)',fontSize:10.5,letterSpacing:'0.1em',textTransform:'uppercase',color:'var(--text3)',fontWeight:400}}>{new Date().toLocaleString('default',{month:'long',year:'numeric'})}</span>
          <div style={{width:1,height:22,background:'var(--border)'}} className="hide-mobile"/>
          <NotificationBell items={myNotifs} onMarkRead={markNotifsRead} onClear={clearNotifs} onOpen={openNotif}/>
          <div style={{display:'flex',alignItems:'center',gap:11}}>
            <div style={{textAlign:'right'}} className="hide-mobile">
              <div style={{fontFamily:'var(--serif)',fontSize:15,fontWeight:400,color:'var(--text)',lineHeight:1.1}}>Admin User</div>
              <div style={{fontFamily:'var(--mono)',fontSize:9,fontWeight:400,letterSpacing:'0.12em',textTransform:'uppercase',color:'var(--text3)',marginTop:2}}>Business Manager</div>
            </div>
            <div className="uav" style={{width:38,height:38,borderRadius:'var(--radius)',background:'transparent',color:'var(--text)',border:'1px solid var(--border2)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:400,fontSize:15,fontFamily:'var(--serif)'}}>AD</div>
          </div>
        </header>

        <main className="page-main">
          <EnableNotifications empId="admin"/>
          <div key={page} className="page-anim">
          {page==='dashboard'  && <DashboardPage store={store} onNavigate={navigate} persist={persist} empId="admin"/>}
          {page==='invoices'   && <InvoicesPage  store={store} persist={persist} showToast={showToast}/>}
          {page==='finance'    && <FinancePage   store={store} persist={persist} showToast={showToast}/>}
          {page==='hr'         && <HRPage        store={store} persist={persist} showToast={showToast}/>}
          {page==='updates'    && <UpdatesPage   store={store} persist={persist} showToast={showToast}/>}
          {page==='holidays'   && <HolidayCalendar store={store} persist={persist} showToast={showToast}/>}
          {page==='reports'    && <ReportsPage   store={store} persist={persist} showToast={showToast} canSeeSocial canSeePerfMarketing/>}
          {page==='letterhead' && <LetterheadPage/>}
          {page==='ads'        && <AdsPage/>}
          {page==='agencies'   && <ParentClientsPage store={store} persist={persist} showToast={showToast}/>}
          {page==='tasks'      && <TasksPage     store={store} persist={persist} showToast={showToast} session={{role:'admin',empId:'admin'}}/>}
          {page==='content'    && <ContentPage   store={store} persist={persist} showToast={showToast} session={{role:'admin',empId:'admin'}} openCalendarId={contentTarget} onOpenConsumed={()=>setContentTarget(null)}/>}
          {page==='attendance' && <CompanyAttendancePage store={store}/>}
          {page==='migrate'    && <MigrationPage store={store}/>}
          </div>
        </main>
      </div>

      <Toast toast={toast}/>
    </div>
  );
}
